import React, { useState, useEffect, useCallback } from 'react';

interface FileUploadProps {
  type: 'images' | 'videos' | 'music';
  title: string;
  accept: string;
}

interface UploadedFile {
  name: string;
  size: string;
  url?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({ type, title, accept }) => {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    fetchFiles();
  }, [type]);

  const fetchFiles = async () => {
    try {
      const response = await fetch(`/api/files/${type}`, {
        credentials: 'include',
      });
      const data = await response.json();
      setFiles(data.files || []);
    } catch (error) {
      console.error('Error fetching files:', error);
    }
  };

  const handleUpload = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;

    setUploading(true);
    setMessage('');
    setError('');

    const formData = new FormData();
    for (let i = 0; i < fileList.length; i++) {
      formData.append('files', fileList[i]);
    }

    try {
      const response = await fetch(`/api/${type}`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(`Successfully uploaded ${fileList.length} file(s)`);
        fetchFiles();
      } else {
        setError(data.error || 'Upload failed');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (filename: string) => {
    if (!window.confirm(`Delete ${filename}?`)) return;

    try {
      const response = await fetch(`/api/files/${type}/${encodeURIComponent(filename)}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        setMessage(`Deleted ${filename}`);
        fetchFiles();
      } else {
        const data = await response.json();
        setError(data.error || 'Delete failed');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    }
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    handleUpload(e.dataTransfer.files);
  }, []);

  return (
    <div className="window" style={{ width: '100%', maxWidth: '800px' }}>
      <div className="title-bar">
        <div className="title-bar-text">{title}</div>
      </div>
      <div className="window-body" style={{ padding: '16px' }}>
        {/* Upload area */}
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          style={{
            border: dragActive ? '2px dashed #000080' : '2px dashed #808080',
            background: dragActive ? '#e0e0ff' : '#f0f0f0',
            padding: '32px',
            textAlign: 'center',
            marginBottom: '16px',
            cursor: 'pointer'
          }}
          onClick={() => document.getElementById(`file-input-${type}`)?.click()}
        >
          <div style={{ fontSize: '48px', marginBottom: '8px' }}>📁</div>
          <p style={{ margin: '8px 0' }}>
            {uploading ? 'Uploading...' : 'Drag & drop files here or click to browse'}
          </p>
          <p style={{ fontSize: '11px', color: '#666', margin: '4px 0' }}>
            Accepted: {accept}
          </p>
          <input
            id={`file-input-${type}`}
            type="file"
            accept={accept}
            multiple
            onChange={(e) => handleUpload(e.target.files)}
            style={{ display: 'none' }}
          />
        </div>

        {/* Messages */}
        {message && (
          <div style={{
            background: '#008000',
            color: '#fff',
            padding: '8px',
            marginBottom: '12px',
            fontSize: '11px'
          }}>
            {message}
          </div>
        )}

        {error && (
          <div style={{
            background: '#ff0000',
            color: '#fff',
            padding: '8px',
            marginBottom: '12px',
            fontSize: '11px'
          }}>
            {error}
          </div>
        )}

        {/* File list */}
        <div style={{ marginTop: '16px' }}>
          <h4 style={{ margin: '0 0 8px 0' }}>Uploaded Files ({files.length})</h4>
          {files.length === 0 ? (
            <p style={{ color: '#666', fontSize: '11px' }}>No files uploaded yet.</p>
          ) : (
            <div style={{ maxHeight: '400px', overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #808080', textAlign: 'left' }}>
                    <th style={{ padding: '8px', fontSize: '11px' }}>Filename</th>
                    <th style={{ padding: '8px', fontSize: '11px' }}>Size</th>
                    <th style={{ padding: '8px', fontSize: '11px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {files.map((file, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #c0c0c0' }}>
                      <td style={{ padding: '8px', fontSize: '11px' }}>{file.name}</td>
                      <td style={{ padding: '8px', fontSize: '11px' }}>{file.size}</td>
                      <td style={{ padding: '8px' }}>
                        <button
                          onClick={() => handleDelete(file.name)}
                          style={{ fontSize: '10px', padding: '2px 8px' }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileUpload;
